const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Intenta renovar el access token usando el refresh cookie.
// Devuelve la Response completa (no solo un booleano) para que el llamador
// pueda distinguir un 401 normal (sesión vencida) de un 403 de trial vencido.
const tryRefresh = async () => {
  const res = await fetch(`${API_URL}/token/refresh/`, {
    method: 'POST',
    credentials: 'include',
  });
  return res;
};

// Wrapper para todas las llamadas autenticadas.
// Reintenta una vez tras 401 refrescando el token automáticamente.
// Si el refresh falla con 403 (trial de profesional vencido, ver
// accounts/token_views.py::CookieTokenRefreshView), guarda el mensaje de
// bloqueo para que la pantalla de login lo muestre en vez de recargar en
// silencio a un formulario vacío.
const fetchWithAuth = async (url, options = {}) => {
  const opts = { ...options, credentials: 'include' };
  let res = await fetch(url, opts);
  if (res.status === 401) {
    const refreshRes = await tryRefresh();
    if (refreshRes.ok) {
      res = await fetch(url, opts);
    } else {
      localStorage.removeItem('user');
      if (refreshRes.status === 403) {
        const data = await refreshRes.json().catch(() => ({}));
        localStorage.setItem('trialExpiredMessage', data.detail || '');
      }
      window.location.reload();
    }
  }
  return res;
};

export const login = async (username, password) => {
  const res = await fetch(`${API_URL}/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.detail || 'Credenciales incorrectas');
    if (res.status === 403) err.trialExpired = true;
    throw err;
  }

  const userRes = await fetch(`${API_URL}/accounts/me/`, { credentials: 'include' });
  const userData = await userRes.json();
  localStorage.setItem('user', JSON.stringify(userData));
  return userData;
};

export const logout = async () => {
  await fetch(`${API_URL}/accounts/logout/`, {
    method: 'POST',
    credentials: 'include',
  });
  localStorage.removeItem('user');
};

export const getConsultations = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/consultations/?patient=${patientId}`);
  if (!res.ok) throw new Error('Error al obtener consultas');
  return await res.json();
};

export const createConsultation = async (patientId, date, notes) => {
  const res = await fetchWithAuth(`${API_URL}/consultations/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patient: patientId, date, notes }),
  });
  if (!res.ok) throw new Error('Error al crear consulta');
  return await res.json();
};

export const deleteConsultation = async (id) => {
  const res = await fetchWithAuth(`${API_URL}/consultations/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar consulta');
};

export const createPatient = async (firstName, lastName) => {
  const res = await fetchWithAuth(`${API_URL}/patients/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ first_name: firstName, last_name: lastName }),
  });
  if (!res.ok) throw new Error('Error al crear paciente');
  return await res.json();
};

export const deletePatient = async (id, password) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${id}/`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.password || 'Error al eliminar paciente');
  }
};

export const getPatients = async ({ showInactive = false, sex = '', area = '' } = {}) => {
  const params = new URLSearchParams();
  if (showInactive) params.set('show_inactive', 'true');
  if (sex) params.set('sex', sex);
  if (area) params.set('area', area);
  const qs = params.toString();
  const res = await fetchWithAuth(`${API_URL}/patients/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error('Error al obtener pacientes');
  return await res.json();
};

// ── Kinesiology API ──────────────────────────────────────
export const getKinesioProfile = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/profile/${patientId}/`);
  if (!res.ok) throw new Error('Error al cargar perfil kinesiológico');
  return await res.json();
};

export const saveKinesioProfile = async (patientId, data) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/profile/${patientId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al guardar perfil kinesiológico');
  return await res.json();
};

// GMFCS — múltiples evaluaciones por paciente
export const getGMFCSList = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/gmfcs/${patientId}/`);
  if (!res.ok) throw new Error('Error al cargar evaluaciones GMFCS');
  return await res.json();
};

export const createGMFCS = async (patientId, data = {}) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/gmfcs/${patientId}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al crear evaluación GMFCS');
  return await res.json();
};

export const updateGMFCS = async (patientId, recordId, data) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/gmfcs/${patientId}/${recordId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al actualizar evaluación GMFCS');
  return await res.json();
};

export const deleteGMFCS = async (patientId, recordId) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/gmfcs/${patientId}/${recordId}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar evaluación GMFCS');
};

// Threshold — múltiples registros por paciente
export const getThresholdList = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/threshold/${patientId}/`);
  if (!res.ok) throw new Error('Error al cargar registros de threshold');
  return await res.json();
};

export const createThreshold = async (patientId, data = {}) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/threshold/${patientId}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al crear registro de threshold');
  return await res.json();
};

export const updateThreshold = async (patientId, recordId, data) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/threshold/${patientId}/${recordId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al actualizar registro de threshold');
  return await res.json();
};

export const deleteThreshold = async (patientId, recordId) => {
  const res = await fetchWithAuth(`${API_URL}/kinesiology/threshold/${patientId}/${recordId}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar registro de threshold');
};

export const getAppointments = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const res = await fetchWithAuth(`${API_URL}/appointments/${params ? '?' + params : ''}`);
  if (!res.ok) throw new Error('Error al obtener citas');
  return await res.json();
};

export const updateAppointment = async (id, data) => {
  const res = await fetchWithAuth(`${API_URL}/appointments/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Error al actualizar turno');
  return await res.json();
};

export const createAppointment = async (patientId, date, time = null, notes = null, title = null) => {
  const body = { date };
  if (patientId) body.patient = patientId;
  if (time) body.time = time;
  if (notes) body.notes = notes;
  if (title) body.title = title;
  const res = await fetchWithAuth(`${API_URL}/appointments/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json();
    const msg = err.time?.[0] || err.non_field_errors?.[0] || err.detail || 'Error al crear cita';
    throw new Error(msg);
  }
  return await res.json();
};

export const deleteAppointment = async (id) => {
  const res = await fetchWithAuth(`${API_URL}/appointments/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar cita');
};

export const getImages = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/images/?patient=${patientId}`);
  if (!res.ok) throw new Error('Error al obtener imágenes');
  return await res.json();
};

export const uploadImage = async (patientId, file) => {
  const formData = new FormData();
  formData.append('patient', patientId);
  formData.append('image', file);
  formData.append('name', file.name);
  const res = await fetchWithAuth(`${API_URL}/images/`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) throw new Error('Error al subir imagen');
  return await res.json();
};

export const deleteImage = async (id) => {
  const res = await fetchWithAuth(`${API_URL}/images/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar imagen');
};

export const uploadSignature = async (file) => {
  const formData = new FormData();
  formData.append('signature', file);
  const res = await fetchWithAuth(`${API_URL}/accounts/me/signature/`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) throw new Error('Error al subir firma');
  return await res.json();
};

export const deleteSignature = async () => {
  const res = await fetchWithAuth(`${API_URL}/accounts/me/signature/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar firma');
  return await res.json();
};

export const getPatientEditData = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/edit-data/`);
  if (!res.ok) throw new Error('Error al cargar datos del paciente');
  return await res.json();
};

export const updatePatientFields = async (patientId, fields) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/fields/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) throw new Error('Error al guardar campos del paciente');
  return await res.json();
};

export const getRecommendations = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/recommendations/`);
  if (!res.ok) throw new Error('Error al obtener recomendaciones');
  return await res.json();
};

export const createRecommendation = async (patientId, { text, date }) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/recommendations/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, date }),
  });
  if (!res.ok) throw new Error('Error al crear recomendación');
  return await res.json();
};

export const updateRecommendation = async (patientId, recId, { text, date }) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/recommendations/${recId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, date }),
  });
  if (!res.ok) throw new Error('Error al actualizar recomendación');
  return await res.json();
};

export const deleteRecommendation = async (patientId, recId) => {
  const res = await fetchWithAuth(`${API_URL}/patients/${patientId}/recommendations/${recId}/`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Error al eliminar recomendación');
};

export const register = async ({ username, password, first_name, last_name, email, profession }) => {
  const res = await fetch(`${API_URL}/accounts/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, first_name, last_name, email, profession }),
  });
  const data = await res.json();
  if (!res.ok) {
    const msg =
      data.username?.[0] ||
      data.email?.[0] ||
      data.password?.[0] ||
      data.non_field_errors?.[0] ||
      'Error al registrar usuario';
    throw new Error(msg);
  }
  return data;
};

// ── Recuperación de contraseña (sin sesión — no pasa por fetchWithAuth) ──
export const requestPasswordReset = async (email) => {
  const res = await fetch(`${API_URL}/accounts/password-reset/request/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.email?.[0] || data.detail || 'Error al pedir el código.');
  return data;
};

export const verifyPasswordResetCode = async (email, code) => {
  const res = await fetch(`${API_URL}/accounts/password-reset/verify/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.code?.[0] || data.detail || 'Código inválido o expirado.');
  return data;
};

export const confirmPasswordReset = async (resetToken, newPassword) => {
  const res = await fetch(`${API_URL}/accounts/password-reset/confirm/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reset_token: resetToken, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.new_password?.[0] || data.reset_token || data.detail || 'Error al actualizar la contraseña.';
    throw new Error(msg);
  }
  return data;
};

export const getMe = async () => {
  const res = await fetchWithAuth(`${API_URL}/accounts/me/`);
  if (!res.ok) throw new Error('Error al obtener el perfil');
  return await res.json();
};

export const updateMe = async (data) => {
  const res = await fetchWithAuth(`${API_URL}/accounts/me/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.username?.[0] || err.email?.[0] || 'Error al actualizar perfil');
  }
  return await res.json();
};

export const changePassword = async (current_password, new_password) => {
  const res = await fetchWithAuth(`${API_URL}/accounts/change-password/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password, new_password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.current_password?.[0] || err.new_password?.[0] || 'Error al cambiar contraseña');
  }
  return await res.json();
};

export const getUsers = async () => {
  const res = await fetchWithAuth(`${API_URL}/accounts/users/`);
  if (!res.ok) throw new Error('Error al obtener usuarios');
  return await res.json();
};

export const updateUser = async (userId, action, extra = {}) => {
  const res = await fetchWithAuth(`${API_URL}/accounts/users/${userId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...extra }),
  });
  if (!res.ok) throw new Error('Error al actualizar usuario');
  return await res.json();
};

// ISAK — soporte múltiples fichas por paciente
export const getISAKList = async (patientId) => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/`);
  if (!res.ok) throw new Error('Error al cargar fichas ISAK');
  return await res.json();
};

export const createISAK = async (patientId, title = '') => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error('Error al crear ficha ISAK');
  return await res.json();
};

export const getISAK = async (patientId, recordId) => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/${recordId}/`);
  if (!res.ok) throw new Error('Error al cargar evaluacion ISAK');
  return await res.json();
};

export const saveISAK = async (patientId, recordId, data) => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/${recordId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  if (!res.ok) throw new Error('Error al guardar evaluacion ISAK');
  return await res.json();
};

export const updateISAKTitle = async (patientId, recordId, title) => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/${recordId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error('Error al actualizar título');
  return await res.json();
};

export const deleteISAK = async (patientId, recordId) => {
  const res = await fetchWithAuth(`${API_URL}/nutrition/isak/${patientId}/${recordId}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al eliminar evaluacion ISAK');
};

// ── Google Calendar (etapa 3a) ───────────────────────────
export const getGoogleCalendarStatus = async () => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/credential/`);
  if (!res.ok) throw new Error('Error al obtener el estado de Google Calendar');
  return await res.json();
};

// Pide la URL de consentimiento de Google y redirige el navegador —
// no hay nada que devolver, la navegación sale de la SPA.
export const iniciarConexionGoogleCalendar = async () => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/auth/`);
  if (!res.ok) throw new Error('Error al iniciar la conexión con Google Calendar');
  const data = await res.json();
  window.location.href = data.authorization_url;
};

export const desconectarGoogleCalendar = async () => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/credential/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Error al desconectar Google Calendar');
};

export const actualizarCalendarioSeleccionado = async (id, selected) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/calendars/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selected }),
  });
  if (!res.ok) throw new Error('Error al actualizar el calendario');
  return await res.json();
};

// ── Google Calendar — bandeja de pendientes (etapa 3b) ───
export const getPendingEventsCount = async () => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/pending/count/`);
  if (!res.ok) throw new Error('Error al obtener el contador de eventos pendientes');
  return await res.json();
};

export const getPendingEvents = async () => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/pending/`);
  if (!res.ok) throw new Error('Error al obtener los eventos pendientes');
  return await res.json();
};

// Mismo criterio de parseo de error que createAppointment (api.js) — el
// choque de solapamiento se propaga tal cual desde AppointmentSerializer
// como {'time': [...]} (ver proyect-back/CLAUDE.md#google-calendar).
export const linkEventToPatient = async (eventId, patientId, applyToSeries = false) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/${eventId}/link/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patient_id: patientId, apply_to_series: applyToSeries }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.time?.[0] || err.non_field_errors?.[0] || err.detail || 'Error al vincular el evento');
  }
  return await res.json();
};

export const linkEventToNewPatient = async (eventId, firstName, lastName, applyToSeries = false) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/${eventId}/link-new-patient/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ first_name: firstName, last_name: lastName, apply_to_series: applyToSeries }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.time?.[0] || err.non_field_errors?.[0] || err.detail || 'Error al crear y vincular el paciente');
  }
  return await res.json();
};

// Crea un Appointment genérico (patient=None, title=event.title) y lo deja
// visible en el calendario de la app — ver linking.mark_event_as_generic en
// el backend. Antes llamaba a /ignore/ y no creaba nada; renombrado junto
// con el endpoint al agregar esa creación (fix post-3c).
export const markEventAsGenericEvent = async (eventId, applyToSeries = false) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/${eventId}/mark-as-event/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apply_to_series: applyToSeries }),
  });
  if (!res.ok) throw new Error('Error al marcar el evento');
  return await res.json();
};

// ── Google Calendar — bandeja agrupada, rechazar y deshacer (etapa 3c) ──
export const getPendingSeries = async (recurringEventId) => {
  const res = await fetchWithAuth(
    `${API_URL}/google-calendar/events/pending/series/?recurring_event_id=${encodeURIComponent(recurringEventId)}`
  );
  if (!res.ok) throw new Error('Error al obtener las fechas de la serie');
  return await res.json();
};

export const rejectEvent = async (eventId, applyToSeries = false) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/${eventId}/reject/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apply_to_series: applyToSeries }),
  });
  if (!res.ok) throw new Error('Error al rechazar el evento');
  return await res.json();
};

// undo/ no toma body. Adjunta err.status (409 = venció la ventana de
// deshacer, ver linking.UNDO_WINDOW_SECONDS en el backend) para que
// ToastDeshacer distinga ese caso de cualquier otro error.
export const undoEventAction = async (eventId) => {
  const res = await fetchWithAuth(`${API_URL}/google-calendar/events/${eventId}/undo/`, {
    method: 'POST',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const error = new Error(data.detail || 'No se pudo deshacer la acción');
    error.status = res.status;
    throw error;
  }
  return await res.json();
};
